import { useEffect, useId, useRef } from "react";
import type { Groove } from "./grooves.js";
import { useGrooveLibrary } from "./useGrooveLibrary.js";

/** The same saved, searchable A–Z library used by Teaching Tools. */
export function GrooveLibraryPicker({ chosen, onChange, disabled = false }: {
  chosen: Groove | null;
  onChange: (groove: Groove | null) => void;
  disabled?: boolean;
}) {
  const library = useGrooveLibrary({ sort: "title" });
  const id = useId();
  const search = useRef<HTMLInputElement>(null);
  const select = useRef<HTMLSelectElement>(null);
  const pagingFromKeyboard = useRef(false);
  useEffect(() => {
    if (!library.loading && !library.hasMore && pagingFromKeyboard.current) {
      // When the last page removes its button, keep focus in the picker.
      if (document.activeElement === document.body) select.current?.focus();
      pagingFromKeyboard.current = false;
    }
  }, [library.loading, library.hasMore]);
  // A new search must not hide the groove already selected for this task.
  const options = chosen && !library.grooves.some(groove => groove.id === chosen.id)
    ? [chosen, ...library.grooves] : library.grooves;

  return <fieldset className="lesson-tool-fieldset lesson-tool-form groove-library-picker" disabled={disabled}>
    <legend>Groove library</legend>
    <label className="lesson-tool-field" htmlFor={`${id}-search`}>
      <span>Search Groove library</span>
      <input ref={search} id={`${id}-search`} type="search" value={library.search}
        onChange={event => library.setSearch(event.target.value)} placeholder="Search Groove library…" />
    </label>
    {library.loading && <p className="small muted" role="status">Loading grooves…</p>}
    {library.error && <div role="alert">
      <p className="error">{library.error}</p>
      <button type="button" className="secondary" onClick={() => {
        // Recovery removes this error button; leave focus on a stable field.
        (options.length ? select.current : search.current)?.focus();
        library.retry();
      }}>Try again</button>
    </div>}
    {!library.loading && !library.error && library.grooves.length === 0 && <p className="empty">
      {library.search.trim() ? `No grooves match “${library.search.trim()}”.` : "No saved grooves yet. Create one in Teaching Tools first."}
    </p>}
    <label className="lesson-tool-field" htmlFor={`${id}-groove`}>
      <span>Groove</span>
      <select ref={select} id={`${id}-groove`} value={chosen?.id ?? ""} disabled={options.length === 0}
        onChange={event => onChange(options.find(groove => groove.id === event.target.value) ?? null)}>
        <option value="">Choose a groove…</option>
        {options.map(groove => <option key={groove.id} value={groove.id}>{groove.title}</option>)}
      </select>
    </label>
    {library.hasMore && <button type="button" className="secondary" aria-disabled={library.loading} onClick={event => {
      if (library.loading) return;
      pagingFromKeyboard.current = document.activeElement === event.currentTarget;
      library.loadMore();
    }}>
      {library.loading ? "Loading…" : "Load more grooves"}
    </button>}
  </fieldset>;
}
